import context, { ISmsService, TextConversationModel, TaskModel, ApiResultModel } from '@truckdown/systems';
import { defineComponent, computed, ref, Ref, onMounted } from 'vue';
import { Constants } from '../../constants';
import * as signalR from '@microsoft/signalr';

interface IData {
    count: Ref<number>;
    unread: Ref<number>;
}

const isCsrOrAdmin = function () {
    if (!context.isAuthenticated) {
        return false;
    }
    return context.isInRole(Constants.Roles.SystemRole)
        || context.isInRole(Constants.Roles.CSRRole);
}

const getData = function () {
    return {
        count: ref<number>(0),
        unread: ref<number>(0)
    } as IData;
}

const getComputed = function (data: IData) {
    return {
        title: computed(() => {
            let conversations = data.count.value + ' conversation' + (data.count.value == 1 ? '' : 's');
            let unread = data.unread.value + ' unread';
            return conversations + ', ' + unread;
        })
    };
}

const setCounts = async function (data: IData) {
    try {
        let service = await context.getService<ISmsService>('ISmsService');
        let res = await service.conversationCount();
        if (res.ok) {
            data.count.value = res.data ?? 0;
            data.unread.value = res.data2 ?? 0;
        } else {
            console.log('Error getting conversation counts');
        }
    }
    catch (err) {
        console.log(err);
        return 0;
    }
}

const initializeWebsocket = function (data: IData) {
    let connection = new signalR.HubConnectionBuilder()
        .withUrl('/systems/csr-hub')
        .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (retryContext: signalR.RetryContext) => {
                if (retryContext.elapsedMilliseconds < 60000) { // Retry for 1 minute
                    return 5000; //try every 5 seconds for 1 min
                } else if (retryContext.elapsedMilliseconds < 900000) {
                    return 10000; //try every 10 seconds for 15 minutes
                } else {
                    return 60000; //try every minute
                }
            }
        })
        .build();

    connection.onreconnecting((error: any) => {
        console.warn("Connection lost. Reconnecting...", error);
    });

    connection.onreconnected(async () => {
        console.log("Connection reestablished.");
        await setCounts(data);
    });

    connection.onclose((error: any) => {
        console.error("Connection closed.", error);
    });

    //TODO: move to somewhere global?
    connection.on('updateSupportConversation', async (val: TextConversationModel) => {
        try {
            context.events.emit('updateSupportConversation', val);
            await setCounts(data);
        }
        catch (err) {
            console.log('Error receiving updateSupportConversation message', err, val);
        }
    });

    connection.on('new-task', async (val: any) => {
        try {
            context.events.emit('new-task', val);
        }
        catch (err) {
            console.log('Error receiving newTask message', err, val);
        }
    });

    connection.on('update-task', async (val: any) => {
        try {
            context.events.emit('update-task', val);
        }
        catch (err) {
            console.log('Error receiving updateTask message', err, val);
        }
    });

    connection.on('update-task-counts', async (val: any) => {
        try {
            context.events.emit('update-task-counts', val);
        }
        catch (err) {
            console.log('Error receiving updateTaskCounts message', err, val);
        }
    });

    connection.start().catch((err: any) => console.log('Websocket connection error', err));
    return connection;
}

export default defineComponent({
    name: 'AdminTasksLink',
    setup: function (props, ctx) {
        let data = getData();
        let computed = getComputed(data);

        if (isCsrOrAdmin()) {
            onMounted(async () => {
                await setCounts(data);
                initializeWebsocket(data);
            });
        }

        return {
            ...data,
            ...computed
        };
    }
});


